import { type ReactElement, useRef, useState } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';

import { RiSearchLine } from '@remixicon/react';

import { ButtonIcon } from '@spotify-internal/encore-web';
import {
  Input,
  SearchField,
  type SearchFieldProps,
} from '@spotify-internal/encore-web/experimental';

import { useDebounced } from '../../hooks/useDebounced';
import {
  FILTER_SHORTCUT_HOTKEY,
  useFilterShortcut,
} from '../../hooks/useFilterShortcut';
import { useFilters } from './provider';

import styles from './FilterSearch.module.css';

interface FilterSearchProps {
  searchCollapsed?: boolean;
  debounceTime?: number;
  placeholder?: string;
  size?: SearchFieldProps['size'];
}

export const FilterSearch = ({
  searchCollapsed = false,
  debounceTime = 300,
  placeholder = 'Filter...',
  size = 'small',
}: FilterSearchProps): ReactElement => {
  const { searchQuery, setQuery } = useFilters();
  const containerRef = useRef<HTMLDivElement>(null);
  const [filterShortcutEnabled] = useFilterShortcut();
  const [expanded, setExpanded] = useState(false);

  const [displayValue, handleChangeDebounced, handleChangeImmediately] =
    useDebounced<string>(searchQuery, setQuery, debounceTime);

  const isCollapsed = searchCollapsed && !expanded && !displayValue;

  useHotkeys(
    FILTER_SHORTCUT_HOTKEY,
    event => {
      if (searchCollapsed && !expanded) {
        event.preventDefault();
        setExpanded(true);
        return;
      }

      const input = containerRef.current?.querySelector<HTMLElement>('input');
      if (!input) return;

      if (document.activeElement === input) return;

      event.preventDefault();
      input.focus();
    },
    { enabled: filterShortcutEnabled, enableOnFormTags: true },
    [searchCollapsed, expanded],
  );

  if (isCollapsed) {
    return (
      <ButtonIcon
        variant="secondary"
        size="small"
        aria-label="search"
        className={styles.collapsedButton}
        onClick={() => setExpanded(true)}
       data-track-source-anchor="1e394917a0;;low">
        <RiSearchLine size={18} />
      </ButtonIcon>
    );
  }

  return (
    <div ref={containerRef} className={styles.root}>
      <SearchField
        size={size}
        aria-label="search"
        clearButtonLabel="Clear search"
        name="filter-bar"
        value={displayValue}
        onChange={handleChangeDebounced}
        onClear={() => handleChangeImmediately('')}
        onSubmit={value => {
          if (typeof value === 'string') handleChangeImmediately(value);
          else value.preventDefault();
        }}
       data-track-source-anchor="9ae1a97b5b">
        <Input
          placeholder={placeholder}
          autoFocus={expanded}
          onBlur={() => {
            if (searchCollapsed && !displayValue) setExpanded(false);
          }}
         data-track-source-anchor="201675aed3;filter-search-input"/>
      </SearchField>
    </div>
  );
};
